import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { getAnnotationColor, getAnnotationIcon } from '../utils/annotationPresentation';
import {
  getAnnotationAlarmStateLabel,
  getAnnotationDescriptionDisplay,
  getAnnotationSeverityLabel,
  getAnnotationUserLabel,
} from '../utils/annotationDescriptionDisplay';
import { getEventDefinitionLabel } from 'home/Activity/helpers/activityEventDisplay';
import {
  TooltipContainer,
  TooltipHeader,
  HeaderLeft,
  IconCircle,
  HeaderTitle,
  TooltipDescription,
  TitleText,
  TooltipMeta,
  MetaItem,
  MetaIcon,
  SeverityPill
} from './TooltipCommon';
import { ClockIcon, UserIcon } from './MetaIcons';

function formatDuration(startTime, endTime) {
  const start = moment(startTime);
  const end = endTime ? moment(endTime) : null;
  const timeFormat = 'DD/MM/YYYY HH:mm';

  if (!endTime || endTime === startTime) {
    return start.isValid() ? start.format(timeFormat) : '-';
  }

  const startStr = start.isValid() ? start.format(timeFormat) : '-';
  const endStr = end && end.isValid() ? end.format(timeFormat) : '-';
  return `${startStr} - ${endStr}`;
}

const AlarmAnnotationTooltip = ({
  annotation,
  position,
  onMouseEnter,
  onMouseLeave
}) => {
  const { t } = useTranslation();
  const typeColor = getAnnotationColor(annotation, { respectSeverity: true });
  const typeIcon = getAnnotationIcon(annotation.type);
  const baseMetadata = annotation.base_metadata || {};
  const alarmState = baseMetadata.state || 'unknown';
  const stateDisplay = getAnnotationAlarmStateLabel(t, alarmState);
  const description = getAnnotationDescriptionDisplay(t, annotation);

  const tooltip = (
    <TooltipContainer
      style={{
        left: position.x,
        top: position.y
      }}
      placement={position.placement || 'top'}
      align={position.align || 'center'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <TooltipHeader>
        <HeaderLeft>
          <IconCircle bg={typeColor}>
            <span aria-hidden="true">{typeIcon}</span>
          </IconCircle>
          <HeaderTitle>
            {t('annotations.alarmModal.alarm', { defaultValue: 'Alarm' })}
            {annotation.severity && (
              <SeverityPill severity={annotation.severity}>
                {getAnnotationSeverityLabel(t, annotation.severity)}
              </SeverityPill>
            )}
          </HeaderTitle>
        </HeaderLeft>
      </TooltipHeader>

      {baseMetadata.template_name && (
        <TitleText>
          {getEventDefinitionLabel(t, baseMetadata.template_name, baseMetadata.template_name.replace(/_/g, ' '))}
        </TitleText>
      )}

      {description && (
        <TooltipDescription>
          {description}
        </TooltipDescription>
      )}

      <TooltipMeta>
        <div className="meta-row">
          <MetaItem>
            <MetaIcon><ClockIcon size={14} /></MetaIcon>
            <span>{formatDuration(
              annotation.start_time,
              annotation.end_time
            )}
            </span>
          </MetaItem>
          <MetaItem>
            <MetaIcon><UserIcon size={14} /></MetaIcon>
            <span>{getAnnotationUserLabel(t, annotation)}</span>
          </MetaItem>
        </div>
        <div className="meta-row">
          <span>{t('annotations.tooltip.state', {
            defaultValue: 'State: {{state}}',
            state: stateDisplay,
          })}
          </span>
        </div>
        {baseMetadata.machine_id && (
          <div className="meta-row">
            <span>{t('annotations.tooltip.machineId', {
              defaultValue: 'Machine ID: {{id}}',
              id: baseMetadata.machine_id,
            })}
            </span>
          </div>
        )}
        {baseMetadata.tag_id && (
          <div className="meta-row">
            <span>{t('annotations.tooltip.tagId', {
              defaultValue: 'Tag ID: {{id}}',
              id: baseMetadata.tag_id,
            })}
            </span>
          </div>
        )}
      </TooltipMeta>

      {/* Bottom actions removed: header contains View Details */}
    </TooltipContainer>
  );

  return createPortal(tooltip, document.body);
};

AlarmAnnotationTooltip.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    end_time: PropTypes.string,
    description: PropTypes.string,
    severity: PropTypes.oneOf(['low', 'medium', 'high', 'critical']),
    created_by: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_at: PropTypes.string,
    source_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    base_metadata: PropTypes.object
  }).isRequired,

  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    placement: PropTypes.string,
    align: PropTypes.string
  }).isRequired,

  showControls: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};

AlarmAnnotationTooltip.defaultProps = {
  showControls: true,
  onMouseEnter: null,
  onMouseLeave: null
};

export default AlarmAnnotationTooltip;
